import { activity_recent, and, desc, eq, type PowerSyncDrizzleDB } from "@stockpilot/powersync";

const DEFAULT_FEED_LIMIT = 200;

/**
 * Local activity feed (PowerSync `activity_recent` stream, ~90 days).
 *
 * The account's SQLite contains ALL its streams at once: the personal scope
 * (`scope_id = user_id`) AND that of ALL its organizations. Multi-organization:
 * the feed must show ONLY activity from the ACTIVE company, so it filters by
 * `scopeId` (= active `organization_id`, or the userId in Personal). Without the
 * filter, viewing company A would let company B's activity leak in (cross-company
 * leak). If `scopeId` is not passed, everything is returned (compatibility).
 *
 * Canonical order: server_ts DESC (tie-break by UUIDv7 id).
 */
export function getActivityFeedQuery(
  db: PowerSyncDrizzleDB,
  options: { limit?: number; scopeId?: string } = {},
) {
  const base = db.select().from(activity_recent);
  const scoped = options.scopeId ? base.where(eq(activity_recent.scope_id, options.scopeId)) : base;
  return scoped
    .orderBy(desc(activity_recent.server_ts), desc(activity_recent.id))
    .limit(options.limit ?? DEFAULT_FEED_LIMIT);
}

/**
 * Local history for a specific entity (for the "History" sheet in the detail
 * view). Deep history (beyond retention) is requested from the API
 * (`GET /api/activity/entity/:type/:id`).
 */
export function getEntityActivityQuery(
  db: PowerSyncDrizzleDB,
  entityType: string,
  entityId: string,
  options: { limit?: number } = {},
) {
  return db
    .select()
    .from(activity_recent)
    .where(
      and(eq(activity_recent.entity_type, entityType), eq(activity_recent.entity_id, entityId)),
    )
    .orderBy(desc(activity_recent.server_ts), desc(activity_recent.id))
    .limit(options.limit ?? DEFAULT_FEED_LIMIT);
}
